import { useState, useEffect, useCallback } from 'react';
import { X, Clock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { API } from './constants';

export function CrmProgrammes() {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('programme');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/crm/email-schedule`);
      const data = await res.json();
      setEmails(data || []);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const cancel = async (id) => {
    try { await fetch(`${API}/api/crm/email-schedule/${id}`, { method: 'DELETE' }); toast.success('Annule'); load(); } catch { toast.error('Erreur'); }
  };

  const filtered = emails.filter(e => filter === 'tous' ? true : e.status === filter);
  const stats = { programme: emails.filter(e => e.status === 'programme').length, envoye: emails.filter(e => e.status === 'envoye').length, erreur: emails.filter(e => e.status === 'erreur').length };

  if (loading) return <div className="flex justify-center py-12"><Loader2 size={20} className="animate-spin text-[#1B2A52]" /></div>;

  return (
    <div className="space-y-3" data-testid="crm-programmes">
      <div className="grid grid-cols-3 gap-2">
        <div className="bg-amber-50 rounded-xl p-3 border border-amber-200 text-center">
          <p className="text-xl font-bold text-amber-600">{stats.programme}</p>
          <p className="text-[10px] text-amber-500">En attente</p>
        </div>
        <div className="bg-green-50 rounded-xl p-3 border border-green-200 text-center">
          <p className="text-xl font-bold text-green-600">{stats.envoye}</p>
          <p className="text-[10px] text-green-500">Envoyes</p>
        </div>
        <div className="bg-red-50 rounded-xl p-3 border border-red-200 text-center">
          <p className="text-xl font-bold text-red-600">{stats.erreur}</p>
          <p className="text-[10px] text-red-500">Erreurs</p>
        </div>
      </div>

      <div className="flex gap-1 bg-gray-100 rounded-lg p-0.5">
        {[{ id: 'programme', label: 'En attente' }, { id: 'envoye', label: 'Envoyes' }, { id: 'erreur', label: 'Erreurs' }, { id: 'tous', label: 'Tous' }].map(f => (
          <button key={f.id} onClick={() => setFilter(f.id)} className={`flex-1 py-1.5 text-xs rounded-md transition-all ${filter === f.id ? 'bg-white shadow-sm text-[#1B2A52] font-bold' : 'text-gray-400'}`}>{f.label}</button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-8 text-gray-400">
          <Clock size={24} className="mx-auto mb-2 opacity-30" />
          <p className="text-sm">Aucun email {filter === 'programme' ? 'programme' : filter === 'envoye' ? 'envoye' : ''}</p>
          <p className="text-xs mt-1">Utilisez le bouton "Programmer" ou "Sequence auto" dans un email IA</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(e => (
            <div key={e.id} className={`bg-white rounded-xl p-3 border ${e.status === 'programme' ? 'border-amber-200' : e.status === 'envoye' ? 'border-green-200' : 'border-red-200'}`} data-testid={`scheduled-email-${e.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold ${e.status === 'programme' ? 'bg-amber-100 text-amber-700' : e.status === 'envoye' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {e.status === 'programme' ? 'PROGRAMME' : e.status === 'envoye' ? 'ENVOYE' : 'ERREUR'}
                    </span>
                    {e.sequence_step && <span className="text-[9px] px-1.5 py-0.5 bg-purple-100 text-purple-600 rounded-full font-bold">SEQ {e.sequence_step}/3</span>}
                  </div>
                  <p className="text-sm font-semibold text-gray-800 truncate">{e.prospect_nom}</p>
                  <p className="text-xs text-gray-500 truncate">{e.subject}</p>
                  <p className="text-[10px] text-gray-400 mt-1 flex items-center gap-1">
                    <Clock size={10} />
                    {new Date(e.send_at).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' })}
                  </p>
                  {e.error && <p className="text-[10px] text-red-500 mt-0.5">{e.error}</p>}
                </div>
                {e.status === 'programme' && (
                  <button onClick={() => cancel(e.id)} className="p-1.5 rounded-lg hover:bg-red-50 text-gray-300 hover:text-red-500" title="Annuler">
                    <X size={14} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
